/**
 * Cookie-consent i18n strings.
 *
 * Every supported locale must define every key. Falling back to English
 * is a GDPR weak spot (a French/Italian visitor must read consent copy
 * in their language, not English) and we ship five locales today.
 *
 * `privacyHref` is locale-aware so the link inside the banner sends the
 * user to the localised privacy policy.
 */

export const COOKIE_MESSAGES = {
  en: {
    privacyHref: 'https://www.prostay.com/privacy-policy/',
    cookiesHref: 'https://www.prostay.com/cookies-policy/',
    banner: {
      overline: 'Cookies',
      title: 'A few cookies, with your permission',
      body: 'We use cookies to keep the site running, understand how it is used, and improve your experience.',
      acceptAll: 'Accept all',
      rejectAll: 'Reject non-essential',
      rejectShort: 'Reject',
      customize: 'Customize',
      learnMore: 'Cookie policy',
    },
    modal: {
      title: 'Cookie preferences',
      intro: 'Choose which categories of cookies Prostay can set on this device. Strictly necessary cookies are always on because the site cannot function without them.',
      privacyNote: 'For full details, read our',
      privacyLink: 'Privacy Policy',
      and: 'and',
      cookiesLink: 'Cookie Policy',
      close: 'Close',
      saveSelection: 'Save selection',
      acceptAll: 'Accept all',
      rejectAll: 'Reject non-essential',
      alwaysOn: 'Always on',
    },
    categories: {
      necessary: {
        title: 'Strictly necessary',
        body: 'Required for the site to work, login state, security, language, and form submissions. Cannot be turned off.',
      },
      functional: {
        title: 'Functional',
        body: 'Remember preferences like your selected language, dismissed announcements, and saved form drafts so the site feels familiar between visits.',
      },
      analytics: {
        title: 'Analytics',
        body: 'Aggregate, anonymous usage data through Google Tag Manager, Google Analytics, and Microsoft Clarity. Helps us identify what is broken or confusing and ship fewer dark corners.',
      },
      marketing: {
        title: 'Marketing',
        body: 'Used to measure the performance of campaigns we run on third-party platforms and to show Prostay ads that match your interests. None of this fires unless you opt in.',
      },
    },
  },

  es: {
    privacyHref: 'https://www.prostay.com/es/privacy-policy/',
    cookiesHref: 'https://www.prostay.com/es/cookies-policy/',
    banner: {
      overline: 'Cookies',
      title: 'Unas cookies, con tu permiso',
      body: 'Usamos cookies para que el sitio funcione, entender cómo se utiliza y mejorar tu experiencia.',
      acceptAll: 'Aceptar todas',
      rejectAll: 'Rechazar no esenciales',
      rejectShort: 'Rechazar',
      customize: 'Personalizar',
      learnMore: 'Política de cookies',
    },
    modal: {
      title: 'Preferencias de cookies',
      intro: 'Elige qué categorías de cookies puede usar Prostay en este dispositivo. Las cookies estrictamente necesarias siempre están activas porque el sitio no funciona sin ellas.',
      privacyNote: 'Para más información, consulta nuestra',
      privacyLink: 'Política de Privacidad',
      and: 'y',
      cookiesLink: 'Política de Cookies',
      close: 'Cerrar',
      saveSelection: 'Guardar selección',
      acceptAll: 'Aceptar todas',
      rejectAll: 'Rechazar no esenciales',
      alwaysOn: 'Siempre activas',
    },
    categories: {
      necessary: {
        title: 'Estrictamente necesarias',
        body: 'Imprescindibles para que el sitio funcione: sesión, seguridad, idioma y envío de formularios. No se pueden desactivar.',
      },
      functional: {
        title: 'Funcionales',
        body: 'Recuerdan preferencias como tu idioma, los avisos que ya has cerrado y los borradores de formularios para que el sitio te resulte familiar entre visitas.',
      },
      analytics: {
        title: 'Analíticas',
        body: 'Datos de uso agregados y anónimos vía Google Tag Manager, Google Analytics y Microsoft Clarity. Nos ayudan a detectar fallos y a mejorar el producto.',
      },
      marketing: {
        title: 'Marketing',
        body: 'Sirven para medir las campañas que hacemos en plataformas externas y mostrarte anuncios de Prostay relevantes. No se activan a menos que las aceptes.',
      },
    },
  },

  de: {
    privacyHref: 'https://www.prostay.com/de/privacy-policy/',
    cookiesHref: 'https://www.prostay.com/de/cookies-policy/',
    banner: {
      overline: 'Cookies',
      title: 'Ein paar Cookies, mit deiner Zustimmung',
      body: 'Wir verwenden Cookies, damit die Website funktioniert, ihre Nutzung verstanden werden kann und das Erlebnis besser wird.',
      acceptAll: 'Alle akzeptieren',
      rejectAll: 'Nicht notwendige ablehnen',
      rejectShort: 'Ablehnen',
      customize: 'Anpassen',
      learnMore: 'Cookie-Richtlinie',
    },
    modal: {
      title: 'Cookie-Einstellungen',
      intro: 'Wähle aus, welche Kategorien von Cookies Prostay auf diesem Gerät setzen darf. Unbedingt notwendige Cookies sind immer aktiv, da die Website ohne sie nicht funktioniert.',
      privacyNote: 'Weitere Informationen findest du in unserer',
      privacyLink: 'Datenschutzerklärung',
      and: 'und',
      cookiesLink: 'Cookie-Richtlinie',
      close: 'Schließen',
      saveSelection: 'Auswahl speichern',
      acceptAll: 'Alle akzeptieren',
      rejectAll: 'Nicht notwendige ablehnen',
      alwaysOn: 'Immer aktiv',
    },
    categories: {
      necessary: {
        title: 'Unbedingt notwendig',
        body: 'Erforderlich für die Funktion der Website: Login-Status, Sicherheit, Sprache und Formularübermittlung. Kann nicht deaktiviert werden.',
      },
      functional: {
        title: 'Funktional',
        body: 'Speichern Einstellungen wie deine Sprachwahl, geschlossene Hinweise und Formularentwürfe, damit dir die Website zwischen Besuchen vertraut bleibt.',
      },
      analytics: {
        title: 'Analyse',
        body: 'Aggregierte, anonyme Nutzungsdaten über Google Tag Manager, Google Analytics und Microsoft Clarity. Helfen uns, kaputte oder verwirrende Stellen zu erkennen und zu beheben.',
      },
      marketing: {
        title: 'Marketing',
        body: 'Messen die Performance von Kampagnen auf externen Plattformen und zeigen dir relevante Prostay-Anzeigen. Wird ausschließlich nach Zustimmung aktiv.',
      },
    },
  },

  fr: {
    privacyHref: 'https://www.prostay.com/fr/privacy-policy/',
    cookiesHref: 'https://www.prostay.com/fr/cookies-policy/',
    banner: {
      overline: 'Cookies',
      title: 'Quelques cookies, avec votre accord',
      body: 'Nous utilisons des cookies pour faire fonctionner le site, comprendre comment il est utilisé et améliorer votre expérience.',
      acceptAll: 'Tout accepter',
      rejectAll: 'Refuser les non essentiels',
      rejectShort: 'Refuser',
      customize: 'Personnaliser',
      learnMore: 'Politique des cookies',
    },
    modal: {
      title: 'Préférences de cookies',
      intro: 'Choisissez les catégories de cookies que Prostay peut déposer sur cet appareil. Les cookies strictement nécessaires sont toujours actifs : le site ne peut pas fonctionner sans eux.',
      privacyNote: 'Pour plus de détails, consultez notre',
      privacyLink: 'Politique de Confidentialité',
      and: 'et notre',
      cookiesLink: 'Politique des Cookies',
      close: 'Fermer',
      saveSelection: 'Enregistrer la sélection',
      acceptAll: 'Tout accepter',
      rejectAll: 'Refuser les non essentiels',
      alwaysOn: 'Toujours actifs',
    },
    categories: {
      necessary: {
        title: 'Strictement nécessaires',
        body: 'Indispensables au fonctionnement du site : session, sécurité, langue et envoi des formulaires. Ne peuvent pas être désactivés.',
      },
      functional: {
        title: 'Fonctionnels',
        body: 'Mémorisent vos préférences (langue, messages fermés, brouillons de formulaires) pour que le site reste familier entre vos visites.',
      },
      analytics: {
        title: 'Mesure d’audience',
        body: 'Données d’usage agrégées et anonymes via Google Tag Manager, Google Analytics et Microsoft Clarity. Nous aident à repérer les bugs et à améliorer le produit.',
      },
      marketing: {
        title: 'Marketing',
        body: 'Servent à mesurer la performance de nos campagnes sur des plateformes tierces et à afficher des publicités Prostay pertinentes. Activés uniquement après votre accord.',
      },
    },
  },

  it: {
    privacyHref: 'https://www.prostay.com/it/privacy-policy/',
    cookiesHref: 'https://www.prostay.com/it/cookies-policy/',
    banner: {
      overline: 'Cookie',
      title: 'Qualche cookie, con il tuo permesso',
      body: 'Usiamo i cookie per far funzionare il sito, capire come viene utilizzato e migliorare la tua esperienza.',
      acceptAll: 'Accetta tutti',
      rejectAll: 'Rifiuta i non essenziali',
      rejectShort: 'Rifiuta',
      customize: 'Personalizza',
      learnMore: 'Politica sui cookie',
    },
    modal: {
      title: 'Preferenze cookie',
      intro: 'Scegli quali categorie di cookie Prostay può impostare su questo dispositivo. I cookie strettamente necessari sono sempre attivi: senza di essi il sito non può funzionare.',
      privacyNote: 'Per maggiori dettagli, consulta la nostra',
      privacyLink: 'Informativa sulla Privacy',
      and: 'e la',
      cookiesLink: 'Politica sui Cookie',
      close: 'Chiudi',
      saveSelection: 'Salva selezione',
      acceptAll: 'Accetta tutti',
      rejectAll: 'Rifiuta i non essenziali',
      alwaysOn: 'Sempre attivi',
    },
    categories: {
      necessary: {
        title: 'Strettamente necessari',
        body: 'Indispensabili al funzionamento del sito: sessione, sicurezza, lingua e invio dei moduli. Non possono essere disattivati.',
      },
      functional: {
        title: 'Funzionali',
        body: 'Memorizzano preferenze come la lingua, gli avvisi già chiusi e le bozze dei moduli, così il sito ti risulta familiare tra una visita e l’altra.',
      },
      analytics: {
        title: 'Analitici',
        body: 'Dati di utilizzo aggregati e anonimi tramite Google Tag Manager, Google Analytics e Microsoft Clarity. Ci aiutano a identificare problemi e a migliorare il prodotto.',
      },
      marketing: {
        title: 'Marketing',
        body: 'Servono a misurare le campagne su piattaforme esterne e a mostrarti annunci Prostay pertinenti. Si attivano solo se acconsenti.',
      },
    },
  },
};

export function getMessagesForLocale(locale) {
  return COOKIE_MESSAGES[locale] || COOKIE_MESSAGES.en;
}
